import React, { useEffect, useRef, useState } from "react";
import { LoaderCircle, CheckCircle2 } from "lucide-react";
import { computeFakeProgressPercent } from "@/lib/progressUtils";

type Props = {
  reportCompleted?: boolean;
  reportCreatedTs?: string | null;
  fallbackDomain?: string;
  doneSubtitle?: string;
};

/**
 * Onboarding-style progress display for step 2.
 * Shows spinner, title, subtitle, progress bar, and percentage.
 * Mirrors the `.onb-scan` UI from onboarding.html.
 */
export function OnboardingProgressDisplay({
  reportCompleted = false,
  reportCreatedTs = null,
  fallbackDomain = "",
  doneSubtitle = "Full access starts today. Choose your plan below to get started.",
}: Props) {
  const isDone = reportCompleted;
  const fallbackStartTsRef = useRef<string>(new Date().toISOString());
  const [progressPercent, setProgressPercent] = useState(0);

  // Keep the in-progress animation self-contained for onboarding.
  // It reaches a soft cap at 93% until onboarding status marks completion.
  useEffect(() => {
    if (isDone) {
      setProgressPercent(100);
      return;
    }

    const createdTs = reportCreatedTs || fallbackStartTsRef.current;

    const tick = () => {
      setProgressPercent(
        computeFakeProgressPercent({ status: "in_progress", created_ts: createdTs }),
      );
    };

    tick();
    const interval = window.setInterval(tick, 500);
    return () => window.clearInterval(interval);
  }, [isDone, reportCreatedTs]);

  const barFill = isDone ? 100 : progressPercent;
  const domainName = fallbackDomain || "your domain";

  return (
    <div className={`onb-progress-display ${isDone ? "done" : ""}`}>
      <div className="onb-progress-spin">
        {isDone ? (
          <CheckCircle2 className="h-8 w-8 flex-shrink-0 text-vb-pass" />
        ) : (
          <LoaderCircle className="h-8 w-8 flex-shrink-0 animate-spin text-vb-brand" />
        )}
      </div>
      <div className="onb-progress-body">
        <div className="onb-progress-title">
          {isDone
            ? "Your trust report is ready"
            : `Building your trust report for ${domainName}`}
        </div>
        <div className="onb-progress-subtitle">
          {isDone
            ? doneSubtitle
            : "Checking all 19 signals AI uses to judge your business. It keeps running while you set up."}
        </div>
        <div className="onb-progress-bar">
          <span className="onb-progress-bar-fill" style={{ width: `${barFill}%` }} />
        </div>
      </div>
      <div className="onb-progress-pct">{isDone ? 100 : progressPercent}%</div>
    </div>
  );
}
